import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Folder,
  FileText,
  ChevronRight,
  Home,
  Search,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { apiClient, GitLabRepository, GitHubFile } from '../../api/client';

interface GitLabFileBrowserProps {
  repository: GitLabRepository;
  onFileSelect: (file: GitHubFile, fullPath: string) => void;
  onNewFileSelect?: (directoryPath: string, fileName: string) => void;
  selectedPath?: string;
  className?: string;
}

interface BreadcrumbItem {
  name: string;
  path: string;
}

export const GitLabFileBrowser: React.FC<GitLabFileBrowserProps> = ({
  repository,
  onFileSelect,
  onNewFileSelect,
  selectedPath,
  className = '',
}) => {
  const [currentPath, setCurrentPath] = useState('');
  const [files, setFiles] = useState<GitHubFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreatingNewFile, setIsCreatingNewFile] = useState(false);
  const [newFileName, setNewFileName] = useState('');

  // Parse current path into breadcrumbs
  const breadcrumbs: BreadcrumbItem[] = [
    { name: repository.project_name, path: '' }
  ];

  if (currentPath) {
    const pathParts = currentPath.split('/').filter(part => part);
    pathParts.forEach((part, index) => {
      const path = pathParts.slice(0, index + 1).join('/');
      breadcrumbs.push({ name: part, path });
    });
  }

  // Load files for current path
  const loadFiles = async (path: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.getGitLabRepositoryFiles(repository.id, path || undefined);
      // Convert GitLab tree items to GitHubFile format for compatibility
      const converted: GitHubFile[] = (response.files || []).map((item: any) => ({
        name: item.name,
        path: item.path,
        type: item.type === 'tree' ? 'dir' : 'file', // GitLab uses tree/blob, convert to dir/file
        size: 0, // GitLab tree doesn't return size
        sha: item.id || '',
        url: '',
        git_url: '',
      }));
      setFiles(converted);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load repository files');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  // Load files when path changes
  useEffect(() => {
    loadFiles(currentPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, repository.id]);

  const navigateToPath = (path: string) => {
    setCurrentPath(path);
    setSearchTerm('');
  };

  const handleFolderClick = (folder: GitHubFile) => {
    navigateToPath(folder.path);
  };

  const handleFileClick = (file: GitHubFile) => {
    onFileSelect(file, file.path);
  };

  const handleCreateNewFile = () => {
    if (!newFileName.trim()) return;
    const fullPath = currentPath ? `${currentPath}/${newFileName}` : newFileName;
    if (onNewFileSelect) {
      onNewFileSelect(currentPath, fullPath);
    }
    setIsCreatingNewFile(false);
    setNewFileName('');
  };

  const handleCancelNewFile = () => {
    setIsCreatingNewFile(false);
    setNewFileName('');
  };

  const filteredFiles = files.filter(file =>
    file.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const groupedFiles = [...filteredFiles].sort((a, b) => {
    if (a.type === 'dir' && b.type === 'file') return -1;
    if (a.type === 'file' && b.type === 'dir') return 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Folder className="h-5 w-5" />
          Browse Repository Files
        </CardTitle>

        {/* Breadcrumb Navigation */}
        <div className="flex items-center gap-1 text-sm text-muted-foreground flex-wrap">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.path}>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => navigateToPath(crumb.path)}
              >
                {index === 0 ? (
                  <div className="flex items-center gap-1">
                    <Home className="h-3 w-3" />
                    {crumb.name}
                  </div>
                ) : (
                  crumb.name
                )}
              </Button>
              {index < breadcrumbs.length - 1 && (
                <ChevronRight className="h-3 w-3" />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Search and Create New File */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search files..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 h-9"
            />
          </div>
          {onNewFileSelect && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsCreatingNewFile(true)}
              className="h-9 px-3"
            >
              <FileText className="h-4 w-4 mr-1" />
              New File
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {error && (
          <div className="p-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span className="text-sm text-muted-foreground">Loading files...</span>
          </div>
        )}

        {isCreatingNewFile && (
          <div className="p-4 border-b bg-muted/50">
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-foreground">
                  Create New File in: {currentPath || '/'}
                </label>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  placeholder="Enter filename (e.g., policies.yaml)"
                  value={newFileName}
                  onChange={(e) => setNewFileName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateNewFile();
                    else if (e.key === 'Escape') handleCancelNewFile();
                  }}
                  className="flex-1"
                  autoFocus
                />
                <div className="flex gap-2 sm:gap-2 sm:flex-shrink-0">
                  <Button size="sm" onClick={handleCreateNewFile} disabled={!newFileName.trim()} className="flex-1 sm:flex-none">
                    Create
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancelNewFile} className="flex-1 sm:flex-none">
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && (
          <div className="max-h-96 overflow-y-auto">
            {groupedFiles.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                {searchTerm ? 'No files match your search' : 'This directory is empty'}
              </div>
            ) : (
              <div className="space-y-1 p-2">
                {groupedFiles.map((file) => (
                  <div
                    key={file.path}
                    className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors hover:bg-accent ${
                      selectedPath === file.path ? 'bg-accent border-l-2 border-primary' : ''
                    }`}
                    onClick={() => file.type === 'dir' ? handleFolderClick(file) : handleFileClick(file)}
                  >
                    <div className="flex-shrink-0">
                      {file.type === 'dir' ? (
                        <Folder className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      ) : (
                        <FileText className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{file.name}</div>
                      {file.type === 'file' && (file.name.endsWith('.yaml') || file.name.endsWith('.yml')) && (
                        <Badge variant="secondary" className="text-xs mt-1">YAML</Badge>
                      )}
                    </div>
                    {file.type === 'dir' && (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
